import {chunk, flatten, partition, uniqBy} from 'lodash';

import {CancelToken} from '../../../../helpers/asyncCancelHelpers';
import {SearchTagStatusesEnum} from '../../../../helpers/models/tagHelpers';
import {ApiSingleTag, ApiTags, TagBehaviorsEnum, TagModel} from '../../../../models/api/tagModel';
import {TeammateModel} from '../../../../models/api/teammateModel';
import {fetchApiAsync} from '../../../../services/http/http';
import {getAvailableNamespaces} from '../../ruleHelpers';

/*
 * Constants.
 */

const TAGS_LIMIT = 100;

/*
 * Types.
 */

interface FetchProps {
  companyUrl: string;
  search?: string;
  namespace: string;
  cancelToken: CancelToken;
  user: TeammateModel;
  teammates: ReadonlyArray<TeammateModel>;
  status?: SearchTagStatusesEnum;
}

interface FetchSingleProps {
  companyUrl: string;
  id: TagModel['id'];
  cancelToken: CancelToken;
}

interface FetchIdsProps {
  companyUrl: string;
  ids: ReadonlyArray<TagModel['id']>;
  cancelToken: CancelToken;
}

/*
 * Service.
 */

export class TagService {
  fetchCache = new Map<string, Promise<ReadonlyArray<TagModel>>>();
  fetchSingleCache = new Map<string, Promise<TagModel>>();
  fetchByIdsCache = new Map<number, Promise<ReadonlyArray<TagModel>>>();

  fetch({
    companyUrl,
    search = '',
    namespace,
    cancelToken,
    user,
    teammates,
  }: FetchProps): Promise<ReadonlyArray<TagModel>> {
    const namespaces = getAvailableNamespaces(namespace, user, teammates).join(',');
    const tagsUrl =
      `${companyUrl}/search_tag2/${search}?admin_override=false&behaviors=${TagBehaviorsEnum.CAN_RULE}&limit=${TAGS_LIMIT}&namespaces=${namespaces}` +
      `&tag_status=${SearchTagStatusesEnum.ACTIVE}`;
    const cacheItem = this.fetchCache.get(tagsUrl);
    if (cacheItem) {
      return cacheItem;
    }

    const request = fetchApiAsync(ApiTags, tagsUrl, {cancelToken});
    request.catch(() => this.fetchCache.delete(tagsUrl));
    this.fetchCache.set(tagsUrl, request);
    return request;
  }

  fetchSingle({companyUrl, id, cancelToken}: FetchSingleProps): Promise<TagModel> {
    const tagUrl = `${companyUrl}/tags/${id}`;
    const cacheItem = this.fetchSingleCache.get(tagUrl);
    if (cacheItem) {
      return cacheItem;
    }

    const request = fetchApiAsync(ApiSingleTag, tagUrl, {cancelToken});
    request.catch(() => this.fetchSingleCache.delete(tagUrl));
    this.fetchSingleCache.set(tagUrl, request);
    return request;
  }

  async fetchByIds({companyUrl, ids, cancelToken}: FetchIdsProps): Promise<ReadonlyArray<TagModel>> {
    if (ids.length === 0) {
      return [];
    }

    const [cachedIds, uncachedIds] = partition(ids, (id) => this.fetchByIdsCache.has(id));

    // paginate
    const chunks = chunk(uncachedIds, TAGS_LIMIT);

    const uncachedRequest = Promise.all(
      chunks.map((chunkIds) => fetchApiAsync(ApiTags, `${companyUrl}/tags/?ids=${chunkIds.join(',')}`), {
        cancelToken,
      }),
    ).then(flatten);

    uncachedRequest.catch(() => uncachedIds.forEach((id) => this.fetchByIdsCache.delete(id)));
    uncachedIds.forEach((id) => this.fetchByIdsCache.set(id, uncachedRequest));

    const requestData = await Promise.all([
      uncachedRequest,
      ...cachedIds.map((id) => this.fetchByIdsCache.get(id)!),
    ]);
    const idsSet = new Set(ids);
    return uniqBy(
      // Flatten data & only pick ids that we have requested
      flatten(requestData).filter((tag) => idsSet.has(tag.id)),
      // One tag per id
      'id',
    );
  }
}
